import { useState, useCallback, useEffect } from 'react';
import SectionWrapper from '@/components/feature/SectionWrapper';
import { Link } from 'react-router-dom';

// Strict grid: only square and portrait ratios, clean masonry pattern
const galleryImages = [
  {
    id: 'g1',
    src: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/66e54a4685373c23b5cd6b9e56713d0f.png',
    alt: 'Heavy bag training',
    size: 'portrait',
  },
  {
    id: 'g2',
    src: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/e45f316ea85d5f4030e426237dbf23c5.png',
    alt: 'Boxing match action',
    size: 'square',
  },
  {
    id: 'g3',
    src: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/8f13d7f970768b112f86632da09f0905.png',
    alt: 'Pad work training',
    size: 'square',
  },
  {
    id: 'g4',
    src: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/158a6584dfc2964cda24644353d11b04.png',
    alt: 'Boxing gloves on ropes',
    size: 'portrait',
  },
  {
    id: 'g5',
    src: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/679751369cd4b384dd70de76de6712f9.png',
    alt: 'Youth training',
    size: 'portrait',
  },
  {
    id: 'g6',
    src: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/acb9a19c446c36608b5fc875d668150f.png',
    alt: 'Boxing ring',
    size: 'square',
  },
];

export default function GallerySection() {
  const [lightbox, setLightbox] = useState<number | null>(null);

  const openLightbox = (index: number) => setLightbox(index);
  const closeLightbox = () => setLightbox(null);

  const nextImage = useCallback(() => {
    if (lightbox === null) return;
    setLightbox((lightbox + 1) % galleryImages.length);
  }, [lightbox]);

  const prevImage = useCallback(() => {
    if (lightbox === null) return;
    setLightbox((lightbox - 1 + galleryImages.length) % galleryImages.length);
  }, [lightbox]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (lightbox === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = lightbox !== null ? 'hidden' : '';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightbox, nextImage, prevImage]);

  return (
    <section className="bg-boxing-darker py-14 md:py-20">
      <div className="max-w-6xl mx-auto px-5 md:px-8 lg:px-12">
        <SectionWrapper className="text-center mb-10 md:mb-14">
          <span className="section-label">/ Inside the Gym</span>
          <h2 className="heading-mixed text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
            <span className="text-highlight">Gallery</span>
          </h2>
          <div className="w-16 h-[2px] bg-boxing-red mx-auto mt-5" />
        </SectionWrapper>

        {/* Clean masonry grid: portrait + square mix with consistent gaps */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-3">
          {/* Row 1: Portrait | Square | Square */}
          <SectionWrapper delay={0} className="row-span-2">
            <GalleryCard img={galleryImages[0]} index={0} open={openLightbox} />
          </SectionWrapper>
          <SectionWrapper delay={80} className="aspect-square">
            <GalleryCard img={galleryImages[1]} index={1} open={openLightbox} />
          </SectionWrapper>
          <SectionWrapper delay={160} className="aspect-square">
            <GalleryCard img={galleryImages[2]} index={2} open={openLightbox} />
          </SectionWrapper>

          {/* Row 2: Square | Portrait | Square */}
          <SectionWrapper delay={240} className="row-span-2">
            <GalleryCard img={galleryImages[3]} index={3} open={openLightbox} />
          </SectionWrapper>
          <SectionWrapper delay={320} className="row-span-2">
            <GalleryCard img={galleryImages[4]} index={4} open={openLightbox} />
          </SectionWrapper>
          <SectionWrapper delay={400} className="aspect-square">
            <GalleryCard img={galleryImages[5]} index={5} open={openLightbox} />
          </SectionWrapper>
        </div>

        <SectionWrapper className="text-center mt-8 md:mt-12">
          <Link to="/gallery" className="btn-secondary text-sm">
            View Full Gallery
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm" />
            </span>
          </Link>
        </SectionWrapper>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 md:p-8"
          onClick={closeLightbox}
        >
          <button
            type="button"
            onClick={closeLightbox}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-boxing-red/80 transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-white text-xl md:text-2xl" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-boxing-red/80 transition-colors cursor-pointer z-10"
          >
            <i className="ri-arrow-left-line text-white text-xl" />
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 rounded-full hover:bg-boxing-red/80 transition-colors cursor-pointer z-10"
          >
            <i className="ri-arrow-right-line text-white text-xl" />
          </button>

          <div
            className="relative max-w-5xl max-h-[85vh] w-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={galleryImages[lightbox].src}
              alt={galleryImages[lightbox].alt}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-xs md:text-sm font-oswald uppercase tracking-wider">
            {lightbox + 1} / {galleryImages.length}
          </div>
        </div>
      )}
    </section>
  );
}

function GalleryCard({
  img,
  index,
  open,
}: {
  img: { src: string; alt: string };
  index: number;
  open: (i: number) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => open(index)}
      className="group relative w-full h-full overflow-hidden rounded-xl cursor-pointer border border-white/5"
    >
      <img
        src={img.src}
        alt={img.alt}
        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-boxing-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
        <span className="w-10 h-10 flex items-center justify-center bg-boxing-red rounded-full shadow-red-glow transition-transform duration-300 group-hover:scale-110">
          <i className="ri-zoom-in-line text-white text-lg" />
        </span>
      </div>
    </button>
  );
}
